/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, Star, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePaymentContext } from "../context/PaymentContext";
import { useDarkMode } from "../context/DarkModeContext";
import { packageService } from "../utils/PackageService";
import type { Package } from "../types/package";
import type { Plan } from "../types/stripe";
import { SkeletonSubscriptionPlans } from "../components/Skeleton";

interface SubscriptionPlansProps {
  onSubscribe?: (pkg: Package, billingPeriod: "monthly" | "annual") => void;
  onMpesaPayment?: (
    pkg: Package,
    isAnnual: boolean,
    phoneNumber: string,
  ) => void;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = () => {
  useAuth();
  const {
    state: paymentState,
    setPlan,
    setBillingPeriod,
  } = usePaymentContext();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  const billingPeriod = paymentState.billingPeriod;

  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchJobSeekerPackages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Always fetch job seeker packages only
        const packagesData = await packageService.getJobSeekerPackages();

        const activePackages = packagesData
          .filter((pkg) => pkg.isActive)
          .sort((a, b) => a.orderIndex - b.orderIndex);

        setPackages(activePackages);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load packages";
        setError(errorMessage);
        console.error("Error fetching job seeker packages:", err);

        // Auto-retry logic
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobSeekerPackages();
  }, [retryCount]);

  const convertPackageToPlan = (pkg: Package): Plan => {
    return {
      id: pkg.id,
      name: pkg.name,
      description: pkg.description || "",
      price: billingPeriod === "annual" ? pkg.annualPrice : pkg.monthlyPrice,
      monthlyPrice: pkg.monthlyPrice,
      annualPrice: pkg.annualPrice,
      maxJobs: pkg.maxJobs,
      durationDays: pkg.durationDays,
      features: pkg.features.map((f) => f.text),
      isPopular: pkg.isPopular,
    };
  };

  const handleGetPremium = (pkg: Package) => {
    const plan = convertPackageToPlan(pkg);
    setPlan(plan, billingPeriod);

    const price =
      billingPeriod === "annual" ? pkg.annualPrice : pkg.monthlyPrice;
    const isAnnual = billingPeriod === "annual";

    navigate("/payment-forms", {
      state: {
        package: pkg,
        price,
        isAnnual,
        packageType: "job_seeker", // Always job_seeker
      },
    });
  };

  const handleWhitelistRequest = () => {
    const whitelistPlan: Plan = {
      id: "whitelist_request",
      name: "Whitelist Request",
      description: "Enterprise whitelist access request",
      monthlyPrice: 0,
      annualPrice: 0,
      price: 0,
      features: [
        "Enterprise features",
        "Whitelisted account",
        "Premium support",
      ],
      isPopular: false,
    };

    setPlan(whitelistPlan, "monthly");
    navigate("/payment-forms");
  };

  const handleBillingPeriodChange = (period: "monthly" | "annual") => {
    setBillingPeriod(period);

    if (paymentState.selectedPlan) {
      setPlan(paymentState.selectedPlan, period);
    }
  };

  const calculateSavings = (
    originalPrice: number,
    currentPrice: number,
  ): number => {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getDurationText = (duration: number) => {
    if (duration === 7) return "7 Days";
    if (duration === 30) return "1 Month";
    if (duration === 90) return "3 Months";
    if (duration === 365) return "1 Year";
    return `${duration} Days`;
  };

  const calculateTotalSavings = () => {
    if (billingPeriod !== "annual") return 0;

    let maxSavings = 0;
    packages.forEach((pkg) => {
      const originalPrice = pkg.originalAnnualPrice;
      const currentPrice = pkg.annualPrice;
      if (originalPrice && currentPrice) {
        const savings = calculateSavings(originalPrice, currentPrice);
        if (savings > maxSavings) maxSavings = savings;
      }
    });
    return maxSavings;
  };

  const isPackageSelected = (pkgId: string) => {
    return paymentState.selectedPlan?.id === pkgId;
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setLoading(true);
  };

  // Determine layout based on number of packages
  const getLayoutType = () => {
    if (packages.length === 1) return "single";
    if (packages.length === 2) return "double";
    return "triple";
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen py-8 transition-colors duration-300 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <SkeletonSubscriptionPlans
          count={3} // Show 3 skeleton cards by default
          theme={isDark ? "dark" : "light"}
          layout="triple"
          showBillingToggle={true}
          showWhitelistCard={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen py-8 transition-colors duration-300 ${
          isDark
            ? 'bg-gradient-to-br from-gray-900 to-gray-800'
            : 'bg-gradient-to-br from-sky-50 to-blue-50'
        }`}
      >
        <Helmet>
          <title>
            Job Seeker Subscription Plans & Pricing | FindWork Kenya
          </title>
          <meta
            name="description"
            content="Choose a FindWork job seeker plan. Browse monthly and annual packages with M-Pesa payment support. Find work faster across Kenya."
          />
          <link rel="canonical" href="https://findwork.ltd/packages" />
        </Helmet>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className={`rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-sky-50'
            }`}
          >
            <div
              className={`w-16 h-16 rounded-full mx-auto mb-4 ${
                isDark
                  ? 'bg-red-900/20 text-red-400'
                  : 'bg-red-100 text-red-600'
              } flex items-center justify-center`}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2
              className={`text-2xl font-bold mb-4 ${
                isDark ? 'text-red-400' : 'text-red-800'
              }`}
            >
              Error Loading Plans
            </h2>
            <p className={`mb-6 ${isDark ? 'text-red-300' : 'text-red-600'}`}>
              {error}
            </p>
            <p
              className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              Retry attempt: {retryCount}/3
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRetry}
                className={`px-6 py-3 rounded-lg font-medium shadow-md transition-all duration-300 ${
                  isDark
                    ? 'bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white'
                    : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white'
                }`}
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300'
                }`}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div
        className={`min-h-screen py-8 transition-colors duration-300 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className={`rounded-2xl shadow-lg border p-8 sm:p-12 max-w-2xl mx-auto ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-50"
            }`}
          >
            <Crown
              size={64}
              className={`mx-auto mb-4 ${
                isDark ? "text-sky-400" : "text-sky-500"
              }`}
            />
            <h2
              className={`text-2xl font-bold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              No Job Seeker Plans Available
            </h2>
            <p className={isDark ? "text-gray-300" : "text-gray-600"}>
              No job seeker subscription plans are currently available. Please
              check back later or contact support for assistance.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/")}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isDark
                    ? "bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white"
                    : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                }`}
              >
                Return to Home
              </button>
              <button
                onClick={() => navigate("/contact-support")}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 transition-colors duration-300 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-sky-50 to-blue-50"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Choose Your Job Seeker Plan
          </h1>
          <p
            className={`text-lg sm:text-xl mb-6 sm:mb-8 px-4 ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Start posting jobs and find the perfect candidates
          </p>

          {/* Billing Toggle */}
          <div className="flex justify-center sm:justify-end mb-6 sm:mb-8">
            <div
              className={`rounded-full p-1.5 shadow-sm border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-sky-100"
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-4">
                <span
                  className={`font-medium text-sm sm:text-base px-2 sm:px-0 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Billing:
                </span>
                <div
                  className={`flex rounded-full p-1 ${
                    isDark ? "bg-gray-700" : "bg-sky-50"
                  }`}
                >
                  <button
                    onClick={() => handleBillingPeriodChange("monthly")}
                    className={`px-4 sm:px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                      billingPeriod === "monthly"
                        ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-105"
                        : isDark
                          ? "text-gray-400 hover:text-sky-400 hover:bg-gray-600"
                          : "text-gray-600 hover:text-sky-600 hover:bg-white"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => handleBillingPeriodChange("annual")}
                    className={`px-4 sm:px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                      billingPeriod === "annual"
                        ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-105"
                        : isDark
                          ? "text-gray-400 hover:text-sky-400 hover:bg-gray-600"
                          : "text-gray-600 hover:text-sky-600 hover:bg-white"
                    }`}
                  >
                    Annual
                    {calculateTotalSavings() > 0 && (
                      <span className="ml-1 sm:ml-2 bg-green-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                        Save {calculateTotalSavings()}%
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Packages Grid */}
        <div
          className={`grid ${
            getLayoutType() === "single"
              ? "grid-cols-1 max-w-2xl mx-auto"
              : getLayoutType() === "double"
                ? "grid-cols-1 md:grid-cols-2"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          } gap-6 sm:gap-8 mb-8 sm:mb-12`}
        >
          {packages.map((pkg) => {
            const price =
              billingPeriod === "annual" ? pkg.annualPrice : pkg.monthlyPrice;
            const originalPrice =
              billingPeriod === "annual"
                ? pkg.originalAnnualPrice
                : pkg.originalMonthlyPrice;
            const duration =
              billingPeriod === "annual"
                ? pkg.annualDuration
                : pkg.monthlyDuration;
            const savings = originalPrice
              ? calculateSavings(originalPrice, price)
              : 0;
            const isPopular = pkg.isPopular;
            const isSelected = isPackageSelected(pkg.id);

            return (
              <div
                key={pkg.id}
                className={`relative rounded-2xl shadow-lg border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                  isSelected
                    ? "border-blue-500 ring-4 ring-blue-100 transform scale-105"
                    : isPopular
                      ? "border-sky-500 transform scale-105"
                      : isDark
                        ? "border-gray-700 bg-gray-800"
                        : "border-sky-100 bg-white"
                } ${isDark && !isSelected && !isPopular ? "bg-gray-800" : ""}`}
              >
                {isSelected && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-blue-500 text-white px-4 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center whitespace-nowrap">
                      <Check size={14} className="mr-1" />
                      Selected
                    </div>
                  </div>
                )}

                {isPopular && !isSelected && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center whitespace-nowrap">
                      <Star size={14} className="mr-1" />
                      Most Popular
                    </div>
                  </div>
                )}

                {savings > 0 && (
                  <div className="absolute -top-3 sm:-top-4 right-4 z-10">
                    <div className="bg-green-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      Save {savings}%
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  <h3
                    className={`text-xl sm:text-2xl font-bold mb-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {pkg.name}
                  </h3>

                  {pkg.description && (
                    <p
                      className={`mb-4 text-sm ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {pkg.description}
                    </p>
                  )}

                  <div className="mb-4">
                    <span
                      className={`text-3xl sm:text-4xl font-bold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatPrice(price)}
                    </span>
                    <span
                      className={`ml-2 text-sm sm:text-base ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      /{billingPeriod === "monthly" ? "month" : "year"}
                    </span>
                    {originalPrice && originalPrice > price && (
                      <div
                        className={`text-sm mt-1 ${
                          isDark ? "text-gray-500" : "text-gray-500"
                        } line-through`}
                      >
                        {formatPrice(originalPrice)}
                      </div>
                    )}
                  </div>

                  {/* Job-specific details */}
                  <div
                    className={`text-sm mb-4 ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {pkg.maxJobs === null ? "Unlimited" : pkg.maxJobs} job posts
                    • {getDurationText(duration)}
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {pkg.features
                      .sort((a, b) => a.orderIndex - b.orderIndex)
                      .map((feature) => (
                        <li key={feature.id} className="flex items-start">
                          <Check
                            size={18}
                            className="text-green-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                          />
                          <span
                            className={`text-sm sm:text-base ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {feature.text}
                          </span>
                        </li>
                      ))}
                  </ul>

                  <button
                    onClick={() => handleGetPremium(pkg)}
                    className={`w-full py-3 px-4 sm:px-6 rounded-lg font-semibold transition-all flex items-center justify-center ${
                      isSelected
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
                        : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md"
                    }`}
                  >
                    {isSelected ? "Proceed to Payment" : `Get ${pkg.name}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Whitelist Request Section */}
        <div
          className={`rounded-2xl shadow-lg border p-6 sm:p-8 text-center ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-50"
          }`}
        >
          <Crown
            size={40}
            className={`mx-auto mb-4 ${
              isDark ? "text-sky-400" : "text-sky-500"
            }`}
          />
          <h3
            className={`text-xl sm:text-2xl font-bold mb-2 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Enterprise & Whitelisted Accounts
          </h3>
          <p
            className={`mb-6 text-sm sm:text-base max-w-2xl mx-auto ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Looking for unlimited job posts and premium features? Request to be
            whitelisted by our admin team for special enterprise privileges.
          </p>
          <button
            onClick={handleWhitelistRequest}
            className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md"
          >
            Request Whitelist Access
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
